import { registerSW } from 'virtual:pwa-register'
import { create } from 'zustand'
import { setPushRegistration } from './push'

// The one service worker is registered here. When a new version is ready the
// app shows "Update available" with Reload, and never reloads under someone
// mid-edit: only the button does.

interface PwaState {
  updateReady: boolean
  offlineReady: boolean
  /** Chrome's deferred install prompt, when the browser offers one. */
  canInstall: boolean
}

export const usePwa = create<PwaState>()(() => ({ updateReady: false, offlineReady: false, canInstall: false }))

let applyUpdate: ((reload?: boolean) => Promise<void>) | null = null

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferredPrompt: BeforeInstallPromptEvent | null = null

export function startPwa(): void {
  if (!('serviceWorker' in navigator)) return
  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh: () => usePwa.setState({ updateReady: true }),
    onOfflineReady: () => usePwa.setState({ offlineReady: true }),
    // push uses this same registration; a second worker is never registered
    onRegisteredSW: (_url, reg) => {
      if (reg) setPushRegistration(reg)
    },
  })
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent
    usePwa.setState({ canInstall: true })
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    usePwa.setState({ canInstall: false })
  })
}

/**
 * Activates the waiting service worker and reloads. Only ever called from the Reload button.
 *
 * The plugin's own path reloads on Workbox's "controlling" event, which in an installed Android
 * app could fail to arrive, leaving the button doing nothing. So the waiting worker is told to
 * take over directly, the page reloads as soon as it does, and reloads regardless after a moment:
 * a tap on Reload always reloads.
 */
export async function reloadForUpdate(): Promise<void> {
  let reloading = false
  const reload = () => {
    if (reloading) return
    reloading = true
    window.location.reload()
  }
  const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined
  const waiting = reg?.waiting
  if (!waiting) {
    // already activated, by another window or by the browser: the next load is the new version
    reload()
    return
  }
  navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true })
  waiting.postMessage({ type: 'SKIP_WAITING' })
  void applyUpdate?.(true)
  window.setTimeout(reload, 2500)
}

/** Shows Chrome's install prompt; resolves true when accepted. */
export async function promptInstall(): Promise<boolean> {
  if (!deferredPrompt) return false
  await deferredPrompt.prompt()
  const { outcome } = await deferredPrompt.userChoice
  if (outcome === 'accepted') {
    deferredPrompt = null
    usePwa.setState({ canInstall: false })
  }
  return outcome === 'accepted'
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

export function isIos(): boolean {
  const ua = navigator.userAgent
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1)
}

/** Web push on iPhone works only once the app is on the Home Screen, on iOS 16.4 or later. */
export function needsHomeScreenForPush(): boolean {
  return isIos() && !isStandalone()
}
